import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IPublicClientApplication } from '@azure/msal-browser';
import { config } from '../../config';
import { trackEvent } from '../../utils/posthog';
import { getAuthToken } from '../../utils/authToken';
import { fetchWithRetry } from '../../utils/fetchWithRetry';
import SplitPeopleStep from './SplitPeopleStep';
import SplitItemsStep from './SplitItemsStep';
import SplitResultsStep from './SplitResultsStep';
import SplitEmailsStep from './SplitEmailsStep';

interface BillSplitFlowProps {
  receiptData: any;
  instance: IPublicClientApplication;
  splitToken: string;
  onComplete: () => void;
  onCancel: () => void;
  splitFromReceiptDetail?: boolean;
  splitReceiptId?: string | null;
}

function BillSplitFlow({
  receiptData,
  instance,
  splitToken: initialSplitToken,
  onComplete,
  onCancel,
  splitFromReceiptDetail = false,
  splitReceiptId = null
}: BillSplitFlowProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // Split flow states
  const [splitStep, setSplitStep] = useState<'people' | 'split' | 'results' | 'emails'>('people');
  const [splitToken, setSplitToken] = useState<string>(initialSplitToken);
  const [splitData, setSplitData] = useState<any>(null);
  const [splitPeople, setSplitPeople] = useState<Array<{person_name: string; person_email?: string}>>([{person_name: ''}, {person_name: ''}]);
  const [splitItems, setSplitItems] = useState<{[key: string]: {[key: string]: string}}>({});
  const [splitModeRatio, setSplitModeRatio] = useState<{[key: string]: boolean}>({});
  const [tipAmount, setTipAmount] = useState<string>('0');
  const [tipSplit, setTipSplit] = useState<{[key: string]: string}>({});
  const [tipRatio, setTipRatio] = useState(true);
  const [payerPersonOrder, setPayerPersonOrder] = useState<number>(1);
  const [contactPersonOrder, setContactPersonOrder] = useState<number>(1);
  const [splitResults, setSplitResults] = useState<any>(null);
  const [isSplitProcessing, setIsSplitProcessing] = useState(false);
  const [splitStatusMessage, setSplitStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Helper to make authenticated API calls
  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = await getAuthToken(instance);
    if (!token) {
      throw new Error('Authentication required');
    }

    const headers = {
      ...options.headers,
      'X-Auth-Token': `Bearer ${token}`
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000);

    try {
      const response = await fetchWithRetry(url, { ...options, headers, signal: controller.signal });
      clearTimeout(timeoutId);
      return response;
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Požiadavka trvala príliš dlho (viac ako 2 minúty)');
      }
      throw error;
    }
  };

  // Helper to show error messages
  const showError = (message: string) => {
    setErrorMessage(message);
    setSplitStatusMessage('');
    setIsSplitProcessing(false);
    setTimeout(() => setErrorMessage(''), 8000);
  };

  // Fetch user profile
  const fetchUserProfile = async () => {
    try {
      const response = await authenticatedFetch(`${config.apiUrl}/getUserProfile`);
      if (response.ok) {
        const data = await response.json();
        return {
          displayName: data.user.displayName || data.user.email || '',
          email: data.user.email || ''
        };
      }
    } catch (error) {
      // Silent fallback
    }
    return null;
  };

  // Initialize split flow on mount
  useEffect(() => {
    const initSplit = async () => {
      // Check if we have an existing split token (edit mode)
      if (initialSplitToken) {
        setIsSplitProcessing(true);
        setSplitStatusMessage('Načítavam rozdelenie...');

        try {
          // Load existing split data
          const response = await authenticatedFetch(`${config.apiUrl}/getSplitData?token=${initialSplitToken}`);
          if (!response.ok) {
            throw new Error('Failed to load split data');
          }

          const data = await response.json();
          setSplitData(data);

          // Load existing people
          if (data.people && data.people.length > 0) {
            const existingPeople = data.people.map((p: any) => ({
              person_name: p.person_name,
              person_email: p.person_email || ''
            }));
            setSplitPeople(existingPeople);
          } else {
            // No people yet - initialize with user profile
            const userProfile = await fetchUserProfile();
            if (userProfile) {
              setSplitPeople([
                {
                  person_name: userProfile.displayName,
                  person_email: userProfile.email
                },
                { person_name: '' }
              ]);
            }
          }

          // Load existing split allocations if they exist
          if (data.existingSplits && data.existingSplits.length > 0) {
            const existingItems: {[key: string]: {[key: string]: string}} = {};
            const existingModes: {[key: string]: boolean} = {};
            const existingTipSplit: {[key: string]: string} = {};
            let totalTipAmount = 0;
            let tipMode = 'ratio';

            data.existingSplits.forEach((split: any) => {
              // Check if this is a tip item (item_id starts with "TIP_")
              if (split.item_id.startsWith('TIP_')) {
                // This is a tip split
                existingTipSplit[split.person] = String(split.ratio_share || split.qty_share || '');
                totalTipAmount += split.amount_share || 0;
                tipMode = split.mode;
              } else {
                // Regular item split
                if (!existingItems[split.item_id]) {
                  existingItems[split.item_id] = {};
                }
                existingItems[split.item_id][split.person] = String(split.qty_share || split.ratio_share || '');
                if (split.mode === 'ratio') {
                  existingModes[split.item_id] = true;
                }
              }
            });

            setSplitItems(existingItems);
            setSplitModeRatio(existingModes);

            // Load tip data if found
            if (totalTipAmount > 0) {
              setTipAmount(String(totalTipAmount));
              setTipRatio(tipMode === 'ratio');
              setTipSplit(existingTipSplit);
            }

            // Check if targetStep was specified in location.state (from receipt detail)
            const targetStep = (location.state as any)?.targetStep;
            if (targetStep === 'people') {
              // User wants to edit people, reset split and go to people step
              await authenticatedFetch(`${config.apiUrl}/resetSplit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token: initialSplitToken })
              });
              setSplitItems({});
              setSplitModeRatio({});
              setTipAmount('0');
              setTipSplit({});
              setSplitStep('people');
            } else if (targetStep === 'split') {
              // User wants to edit split allocations
              setSplitStep('split');
            } else {
              // No target specified - if we have split allocations, start on split step, otherwise people step
              setSplitStep(Object.keys(existingItems).length > 0 ? 'split' : 'people');
            }
          } else {
            // No allocations yet, start on people step
            setSplitStep('people');
          }

          // Load payer and contact info
          if (data.session?.payer_person_order) {
            setPayerPersonOrder(data.session.payer_person_order);
          }
          if (data.session?.contact_person_order) {
            setContactPersonOrder(data.session.contact_person_order);
          }

        } catch (error) {
          console.error('Error loading split data:', error);
          // Fallback to people step
          setSplitStep('people');
        } finally {
          setIsSplitProcessing(false);
          setSplitStatusMessage('');
        }
      }
      // Check if we're coming from receipt detail page
      else if (location.state && (location.state as any).fromReceiptDetail) {
        const state = location.state as any;
        setIsSplitProcessing(true);
        setSplitStatusMessage('Načítavam profil...');

        try {
          const userProfile = await fetchUserProfile();
          if (userProfile) {
            setSplitPeople([
              {
                person_name: userProfile.displayName,
                person_email: userProfile.email
              },
              { person_name: '' }
            ]);
          }
        } catch (error) {
          // Silent fallback
        } finally {
          setIsSplitProcessing(false);
          setSplitStatusMessage('');
        }
      } else {
        // If no token provided, initialize with user profile (new split)
        setIsSplitProcessing(true);
        setSplitStatusMessage('Načítavam profil...');

        try {
          const userProfile = await fetchUserProfile();
          if (userProfile) {
            setSplitPeople([
              {
                person_name: userProfile.displayName,
                person_email: userProfile.email
              },
              { person_name: '' }
            ]);
          }
        } catch (error) {
          // Silent fallback
        } finally {
          setIsSplitProcessing(false);
          setSplitStatusMessage('');
        }
      }
    };

    initSplit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize tip split with default values when tip amount is entered
  useEffect(() => {
    if (parseFloat(tipAmount) > 0 && splitData?.people && Object.keys(tipSplit).length === 0) {
      // Initialize with "1" for each person in ratio mode, or "0" in exact mode
      const defaultTipSplit: {[key: string]: string} = {};
      splitData.people.forEach((person: any) => {
        const personKey = `P${person.person_order}`;
        defaultTipSplit[personKey] = tipRatio ? '1' : '0';
      });
      setTipSplit(defaultTipSplit);
    }
  }, [tipAmount, splitData, tipSplit, tipRatio]);

  // Handle back/cancel
  const handleBackFromSplit = () => {
    if (splitStep !== 'emails') {
      trackEvent('split_abandoned', {
        step: splitStep,
        people_count: splitPeople.filter(p => p.person_name.trim()).length
      });
    }

    // Navigate back to receipt detail if we have a receipt ID
    const receiptId = splitReceiptId || receiptData?.receipt_id;
    if (receiptId) {
      navigate(`/receipts/${receiptId}`);
    } else {
      onCancel();
    }
  };

  // Handle save people (people step -> items step)
  const handleSavePeople = async () => {
    const validPeople = splitPeople.filter(p => p.person_name.trim());
    if (validPeople.length < 2) {
      showError('Zadajte minimálne 2 osoby');
      return;
    }

    setIsSplitProcessing(true);
    setSplitStatusMessage('Ukladám osoby...');

    try {
      const response = await authenticatedFetch(`${config.apiUrl}/saveSplitPeople`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: splitToken,
          people: validPeople
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save people');
      }

      // Load split data
      const dataResponse = await authenticatedFetch(`${config.apiUrl}/getSplitData?token=${splitToken}`);
      if (!dataResponse.ok) {
        throw new Error('Failed to load split data');
      }

      const splitDataResult = await dataResponse.json();
      setSplitData(splitDataResult);
      setSplitStep('split');

      trackEvent('split_people_added', {
        people_count: validPeople.length,
        has_emails: validPeople.filter(p => p.person_email).length
      });

    } catch (error: any) {
      showError(error?.message || "Chyba pri ukladaní osôb");
    } finally {
      setIsSplitProcessing(false);
      setSplitStatusMessage('');
    }
  };

  // Handle process split (items step -> results step)
  const handleProcessSplit = async () => {
    setIsSplitProcessing(true);
    setSplitStatusMessage('Spracovávam rozdelenie...');

    try {
      const payload = {
        token: splitToken,
        payer_person_order: payerPersonOrder,
        contact_person_order: contactPersonOrder,
        tip_amount: parseFloat(tipAmount) || 0,
        items: splitItems,
        mode_ratio: splitModeRatio,
        tip_split: tipSplit,
        tip_ratio: tipRatio
      };

      const response = await authenticatedFetch(`${config.apiUrl}/processSplit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to process split');
      }

      // Load results
      const resultsResponse = await authenticatedFetch(`${config.apiUrl}/getSplitResults?token=${splitToken}`);
      if (!resultsResponse.ok) {
        throw new Error('Failed to load split results');
      }

      const results = await resultsResponse.json();
      setSplitResults(results);
      setSplitStep('results');

      trackEvent('split_items_allocated', {
        items_allocated: Object.keys(splitItems).length,
        tip_amount: parseFloat(tipAmount) || 0,
        people_count: splitPeople.filter(p => p.person_name.trim()).length
      });

    } catch (error: any) {
      showError(error.message || 'Chyba pri spracovaní rozdelenia');
    } finally {
      setIsSplitProcessing(false);
      setSplitStatusMessage('');
    }
  };

  // Handle send emails (results step -> emails step)
  const handleSendEmails = async () => {
    console.log('🔴 handleSendEmails called');
    setIsSplitProcessing(true);
    setSplitStatusMessage('Odosielam emaily...');

    try {
      console.log('🔴 Sending email request...');
      const response = await authenticatedFetch(`${config.apiUrl}/sendSplitEmails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: splitToken
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send emails');
      }

      const result = await response.json();
      console.log('🔴 Email sent successfully, result:', result);
      setSplitResults(result);
      console.log('🔴 Setting split step to emails');
      setSplitStep('emails');

      trackEvent('split_completed', {
        people_count: splitPeople.filter(p => p.person_name.trim()).length,
        emails_sent: result.emailsSent || splitPeople.filter(p => p.person_email).length
      });

    } catch (error) {
      console.log('🔴 ERROR sending emails:', error);
      showError(`Chyba pri odosielaní emailov: ${error}`);
    } finally {
      console.log('🔴 handleSendEmails finished');

      setIsSplitProcessing(false);
      setSplitStatusMessage('');
    }
  };

  // Handle edit people from results page
  const handleEditPeople = async () => {
    // Reset the split first so we can edit people
    setIsSplitProcessing(true);
    setSplitStatusMessage('Resetujem rozdelenie...');

    try {
      const response = await authenticatedFetch(`${config.apiUrl}/resetSplit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: splitToken
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to reset split');
      }

      setSplitStep('people');
      setSplitResults(null);
      setSplitItems({});
      setSplitModeRatio({});
      setTipAmount('0');
      setTipSplit({});

    } catch (error) {
      showError(`Chyba pri resetovaní: ${error}`);
    } finally {
      setIsSplitProcessing(false);
      setSplitStatusMessage('');
    }
  };

  // Handle edit items from results page
  const handleEditItems = () => {
    setSplitStep('split');
    setSplitResults(null);
  };

  // Handle reset split (go back to people step)
  const handleResetSplit = async () => {
    if (!window.confirm('Skutočne chcete resetovať rozdelenie? Všetky zmeny budú stratené.')) {
      return;
    }

    setIsSplitProcessing(true);
    setSplitStatusMessage('Resetujem rozdelenie...');

    try {
      const response = await authenticatedFetch(`${config.apiUrl}/resetSplit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: splitToken
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to reset split');
      }

      setSplitStep('people');
      setSplitItems({});
      setSplitModeRatio({});
      setTipAmount('0');
      setTipSplit({});
      setSplitResults(null);

    } catch (error) {
      showError(`Chyba pri resetovaní: ${error}`);
    } finally {
      setIsSplitProcessing(false);
      setSplitStatusMessage('');
    }
  };

  // Handle copy summary
  const handleCopySummary = async () => {
    if (!splitResults || !splitData) return;

    const { results, summary } = splitResults;
    const receipt = splitData.receipt;

    let summaryText = `🧾 ROZDELENIE BLOČKA\n\n`;
    summaryText += `📍 ${receipt?.merchant || 'Obchod'}\n`;
    summaryText += `📅 ${receipt?.datetime ? new Date(receipt.datetime).toLocaleDateString('sk-SK') : ''}\n`;
    summaryText += `💰 Celkom: ${summary.original_total.toFixed(2)} EUR`;
    if (summary.tips_total > 0) {
      summaryText += ` + ${summary.tips_total.toFixed(2)} EUR tringelt`;
    }
    summaryText += `\n\n👥 ROZDELENIE:\n`;

    results.forEach((result: any) => {
      summaryText += `• ${result.person_name}`;
      if (result.is_payer) summaryText += ` (platil)`;
      summaryText += `: ${result.total_amount.toFixed(2)} EUR\n`;
      if (result.tips_amount > 0) {
        summaryText += `  (${result.items_amount.toFixed(2)} EUR položky + ${result.tips_amount.toFixed(2)} EUR tringelt)\n`;
      }
    });

    summaryText += `\n✅ ${summary.is_verified ? 'Overenie: OK' : '⚠️ Overenie: Nesúhlasí'}`;
    summaryText += `\n📧 Emaily: ${summary.email_count}/${results.length} osôb`;
    summaryText += `\n\n🤖 Vygenerované cez SmartBloček`;

    try {
      await navigator.clipboard.writeText(summaryText);
      setSplitStatusMessage('✅ Rozdelenie bolo skopírované do schránky');
      setTimeout(() => setSplitStatusMessage(''), 4000);

      trackEvent('split_results_copied', {
        people_count: results.length,
        total_amount: summary.original_total + summary.tips_total
      });
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = summaryText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setSplitStatusMessage('✅ Rozdelenie bolo skopírované do schránky');
      setTimeout(() => setSplitStatusMessage(''), 4000);

      trackEvent('split_results_copied', {
        people_count: results.length,
        total_amount: summary.original_total + summary.tips_total,
        method: 'fallback'
      });
    }
  };

  // Render appropriate step
  if (splitStep === 'people') {
    return (
      <SplitPeopleStep
        splitPeople={splitPeople}
        setSplitPeople={setSplitPeople}
        isSplitProcessing={isSplitProcessing}
        splitStatusMessage={splitStatusMessage}
        errorMessage={errorMessage}
        onSavePeople={handleSavePeople}
        onBackFromSplit={handleBackFromSplit}
        getAuthToken={() => getAuthToken(instance)}
      />
    );
  }

  if (splitStep === 'split' && splitData) {
    return (
      <SplitItemsStep
        splitData={splitData}
        splitItems={splitItems}
        setSplitItems={setSplitItems}
        splitModeRatio={splitModeRatio}
        setSplitModeRatio={setSplitModeRatio}
        tipAmount={tipAmount}
        setTipAmount={setTipAmount}
        tipSplit={tipSplit}
        setTipSplit={setTipSplit}
        tipRatio={tipRatio}
        setTipRatio={setTipRatio}
        payerPersonOrder={payerPersonOrder}
        setPayerPersonOrder={setPayerPersonOrder}
        contactPersonOrder={contactPersonOrder}
        setContactPersonOrder={setContactPersonOrder}
        isSplitProcessing={isSplitProcessing}
        splitStatusMessage={splitStatusMessage}
        errorMessage={errorMessage}
        onProcessSplit={handleProcessSplit}
        onBackToPeople={() => setSplitStep('people')}
      />
    );
  }

  if (splitStep === 'results' && splitResults) {
    return (
      <SplitResultsStep
        splitResults={splitResults}
        splitData={splitData}
        payerPersonOrder={payerPersonOrder}
        setPayerPersonOrder={setPayerPersonOrder}
        contactPersonOrder={contactPersonOrder}
        setContactPersonOrder={setContactPersonOrder}
        isSplitProcessing={isSplitProcessing}
        splitStatusMessage={splitStatusMessage}
        onSendEmails={handleSendEmails}
        onResetSplit={handleResetSplit}
        onCopySummary={handleCopySummary}
        onBackFromSplit={handleBackFromSplit}
        onEditPeople={handleEditPeople}
        onEditItems={handleEditItems}
      />
    );
  }

  if (splitStep === 'emails') {
    return (
      <SplitEmailsStep
        splitResults={splitResults}
        onBackToReceipt={() => {
          // Navigate to receipt detail
          const receiptId = splitReceiptId || receiptData?.receipt_id;
          console.log('🟣 Navigating to receipt detail, receiptId:', receiptId);
          console.log('🟣 splitReceiptId:', splitReceiptId);
          console.log('🟣 receiptData:', receiptData);

          if (receiptId) {
            console.log('🟣 Calling navigate to /receipts/' + receiptId);
            // Navigate directly - this will unmount the split flow and load the detail page
            navigate(`/receipts/${receiptId}`);
          } else {
            console.log('🟣 No receiptId found, calling onCancel to return to scanner');
            onCancel();
          }
        }}
      />
    );
  }

  return null;
}

export default BillSplitFlow;
